import { Card, CardHeader, Divider, Box, IconButton, Tooltip, Typography, Link } from "@mui/material";
import { ListContextProvider, useListContext } from "ra-core";
import { FC, useEffect } from "react";
import { RecPatch } from "@/dataProvider/tools";
import { EncryptionResponseDto, EncryptionStatus } from "@/gen/api/encrypt";
import { Datagrid } from "../list/datagrid";
import Scrollbar from "../Scrollbar";
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { FunctionField } from "../field";
import DownloadIcon from '@mui/icons-material/Download';
import { useObfuscationDetailContext } from "@/pages/obfuscation/ObfuscationDetail";
import DateField2 from "../field/DateField2";
import LogViewer, { LogViewerProvider, useLogViewerContext, useLogViewerController } from "../backups/LogViewer";
import useLocalListController from "../list/useLocalListController";
import SizeTextField from "../field/SizeTextField";
import { pick } from "lodash";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { SCHEDULE_ENCRYPTION_OBFUSCATION } from "@/constants";


export interface HistoricalObfuscationTableProps {
  databaseId?: number;
}

const listOptions = {
  sorters: {
    id: (a:any, b:any) => b.id - a.id,
  }
}

const HistoricalObfuscationTable: FC<HistoricalObfuscationTableProps> = ({
  databaseId
}) => {
  const listCtrl = useLocalListController({ resource: 'obfuscation-historic-recs', basePath: '', filter: { databaseId } }, listOptions)
  const { refetch } = listCtrl

  useEffect(() => {
    const timer = setInterval(() => {
      refetch()
    }, 12000)
    return () => {
      timer && clearInterval(timer)
    }
  }, [refetch])

  const logViewerCtrl = useLogViewerController()
  return (
    <LogViewerProvider value={logViewerCtrl}>
      <ListContextProvider value={listCtrl}>
        <HistoricalObfuscationTableBody />
      </ListContextProvider>
    </LogViewerProvider>
  )
}

const HistoricalObfuscationTableBody: FC = () => {
  const hasWritePermit = useHasPermit([SCHEDULE_ENCRYPTION_OBFUSCATION])
  const listCtx = useListContext<EncryptionResponseDto & RecPatch>()
  const { fireConfigFileDownload } = useObfuscationDetailContext()
  const { showLogInViewer } = useLogViewerContext()
  
  const format = useUserTimeFormat()
  return (
    <Card>
      <CardHeader title="Historical Obfuscation" />
      <Divider />
      <Scrollbar options={{ suppressScrollY: true }}>
        <Box sx={{ minWidth: 1150 }}>
          <Datagrid 
            hasBulkActions={false} 
            sx={{ 
              '& th' : { py: 2 },
              '& td' : { py: 2 },
            }}
            {...listCtx}
          >
            <DateField2 source="backupCreatedTime" label="Backup Timestamp" sortable={false} showTime />
            <SizeTextField source="backupSizeText" numericSource="backupSize" label="Backup Size" emptyText="N/A" sortable={false} />
            <DateField2 source="startTime" label="Start Time" sortable={false} showTime />
            <FunctionField 
              source="endTime" 
              label="End Time" 
              sortable={false}
              render={(rec?: EncryptionResponseDto & RecPatch) => (
                <Typography
                  sx={{
                    color:
                      rec?.status === EncryptionStatus.Failed ? 'error.main'
                      : rec?.status === EncryptionStatus.Success ? 'success.main'
                      : rec?.status === EncryptionStatus.Warning ? 'warning.main'
                      : 'inherit',
                    }}
                  variant="body2"  
                >
                  {rec?.status === EncryptionStatus.NotStarted ? ''
                  : rec?.status === EncryptionStatus.InProgress ? 'In Progress'
                  : rec?.endTime && format(rec.endTime, 'Pp')}
                </Typography>
              )}
            />
            <SizeTextField source="sizeText" numericSource="size" label="Encrypted Size" emptyText="N/A" sortable={false} />
            <FunctionField 
              source="status" 
              label="Status" 
              sortable={false} 
              render={(rec?: EncryptionResponseDto & RecPatch) => (
                <Box sx={{ display: 'flex', alignContent: 'center'}}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      bgcolor: 
                        rec?.status === EncryptionStatus.Failed ? 'error.main'
                        : rec?.status === EncryptionStatus.Success ? 'success.main'
                        : rec?.status === EncryptionStatus.Warning ? 'warning.main'
                        : 'inherit',
                      mr: 1,
                    }}
                  />
                  {rec && (rec.status === EncryptionStatus.Failed || rec.status === EncryptionStatus.Warning) &&
                    <Link
                      component="button"
                      color="secondary"
                      underline="hover"
                      onClick={(e:any) => 
                        showLogInViewer(e, [
                          rec.exception ? ['Obfuscation Exception', rec.exception] : null,
                          rec.encryptionFailedLog ? [
                            'Obfuscation   Error  ', 
                            JSON.stringify(
                              JSON.parse(rec.encryptionFailedLog).map((o:any) => pick(o, ['Schema', 'TableName', 'ErrorMessage'] )), null, 2)
                          ] : null,
                          rec.backupLog ? ['Backup     Log      ', rec.backupLog] : null,
                          rec.restoreLog ? ['Restore     Log     ', rec.restoreLog] : null,
                        ].flatMap(pair => {
                          if (!pair) return []
                          return [
                            '########################',
                            `# ${pair[0]} #`,
                            '########################',
                            '',
                            pair[1],
                            ''
                          ]
                        }).join('\n'))
                      }
                      sx={{ cursor: 'pointer' }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                      >
                        (View Log)

                      </Typography>
                    </Link> 
                  }
                </Box>
              )}
            />
            {hasWritePermit &&
              <FunctionField 
                source="downloadConfigFile" 
                label="Action" 
                sortable={false} 
                render={(rec?: EncryptionResponseDto & RecPatch) => (
                  rec && rec.status === EncryptionStatus.Success && !rec.isFileDeleted &&
                  
                  <Tooltip title="Download Config File">
                    <IconButton 
                      sx={{ 
                        color: 'text.primary' as any, 
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                      onClick={() => 
                        fireConfigFileDownload(rec?.databaseId || 0, rec?.encryptionId || 0)
                      }
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              />}
          </Datagrid>
        </Box>
      </Scrollbar>
      {//@ts-ignore 
      <DefaultPagination {...listCtx} />}
      <LogViewer />
    </Card>
  )
}

export default HistoricalObfuscationTable;
